/**
 * HR-portal FTUE — People steps (`/people`).
 *
 * Owns the on-page coachmarks of the `portal-hr-activation` tour that land on
 * `PortalEmployeesPage`. The page has two mutually-exclusive shapes, so the
 * steps branch the same way the Learn / Coach-roster tours do:
 *
 *  - `peopleEmpty` — its own phase (one concept). Anchors the empty-state card
 *    shown when the org has no people yet AND no filters are applied.
 *  - `peopleFilters` / `peopleTable` — a 2-step group sharing
 *    `PORTAL_HR_PHASE.peopleReady` (chromes "1 of 2" / "2 of 2"). Anchors the
 *    filter toolbar and then the results card, shown once there are people (or
 *    filters are applied). `peopleTable` additionally needs actual rows on
 *    screen (`hasEmployees`) since a filtered-to-empty result still renders the
 *    empty-state card, not the table.
 *
 * Mounted from `PortalEmployeesPage`. The layout hook (`use-portal-hr-ftue`)
 * owns the flow start and the sidebar nav step that leads here; this hook only
 * adds the follow-up steps (orders 27-29) so their `condition`s can close over
 * the page's live state, matching the job-seeker pattern
 * (`use-opportunities-ftue`, `use-learn-ftue`).
 */

import { getStep, registerSteps, useFtueAnchor, useFtueStore } from '@/ftue'
import { useEffect, useRef } from 'react'
import {
  PORTAL_HR_ACTIVATION_FLOW,
  PORTAL_HR_ANCHOR,
  PORTAL_HR_ORDER,
  PORTAL_HR_PHASE,
  PORTAL_HR_STEP,
  onPortalPath,
  portalStep,
  reevaluatePortalHrFlow,
} from './flow'

const PEOPLE_PATH = '/people'

/** This hook's own step ids, for the re-eval effect below. */
const OWN_STEP_IDS: readonly string[] = [
  PORTAL_HR_STEP.peopleEmpty,
  PORTAL_HR_STEP.peopleFilters,
  PORTAL_HR_STEP.peopleTable,
]

export interface PortalPeopleFtueArgs {
  /** The employees query has resolved (`!isLoading`). Steps hold until then so
   *  the coachmark never renders over the loading spinner. Defaults to `true`. */
  ready?: boolean
  /** Any search / department / role filter is applied (`Boolean(search || …)`). */
  hasFilters?: boolean
  /** At least one person is rendered in the results table. Distinct from
   *  `hasFilters`: a filtered-to-empty result shows the empty-state card, not
   *  the table, so `peopleTable` needs this rather than just `hasFilters`. */
  hasEmployees?: boolean
}

export function usePortalPeopleFtue({
  ready = true,
  hasFilters = false,
  hasEmployees = false,
}: PortalPeopleFtueArgs = {}) {
  const emptyAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.peopleEmpty)
  const filtersAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.peopleFilters)
  const tableAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.peopleTable)

  // Live page state for the step `condition`s (registered once below, so the
  // closures must read the current value rather than the mount-time snapshot).
  const readyRef = useRef(ready)
  readyRef.current = ready
  const hasFiltersRef = useRef(hasFilters)
  hasFiltersRef.current = hasFilters
  const hasEmployeesRef = useRef(hasEmployees)
  hasEmployeesRef.current = hasEmployees

  // Register the on-page steps once. Registration is idempotent (keyed by id)
  // and does not itself trigger evaluation.
  useEffect(() => {
    registerSteps([
      portalStep({
        id: PORTAL_HR_STEP.peopleEmpty,
        // Own phase: the mutually-exclusive alternative to the ready branch
        // below, so it should never chrome as part of that pair's "N of M".
        phase: PORTAL_HR_STEP.peopleEmpty,
        order: PORTAL_HR_ORDER.peopleEmpty,
        trigger: { type: 'immediate' },
        // The empty-state card only renders with no people AND no filters. Also
        // gated on the path so an immediate step whose anchor is off-route never
        // blocks the rest of the flow (see the note in `use-resume-upload-ftue`).
        condition: () =>
          onPortalPath(PEOPLE_PATH) &&
          readyRef.current &&
          !hasFiltersRef.current &&
          !hasEmployeesRef.current,
        anchorId: PORTAL_HR_ANCHOR.peopleEmpty,
        placement: 'bottom',
        content: {
          body: 'No one here yet -- import your people to get started.',
          dismissLabel: 'Got it',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.peopleFilters,
        // Shared phase with peopleTable below: a 2-step "you have people, here's
        // how to work with them" group, so it chromes as "1 of 2" (mirrors the
        // Learn tour's empty-branch group). skipScope stays the portalStep()
        // default ('phase'), so skipping this pair retires only the pair.
        phase: PORTAL_HR_PHASE.peopleReady,
        order: PORTAL_HR_ORDER.peopleFilters,
        trigger: { type: 'immediate' },
        // The filter toolbar is always on screen once the query has resolved;
        // show this step whenever there are people or a filter is active.
        condition: () =>
          onPortalPath(PEOPLE_PATH) &&
          readyRef.current &&
          (hasEmployeesRef.current || hasFiltersRef.current),
        anchorId: PORTAL_HR_ANCHOR.peopleFilters,
        placement: 'bottom',
        content: {
          body: 'Search or filter by department and role to find who you are looking for.',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.peopleTable,
        phase: PORTAL_HR_PHASE.peopleReady,
        order: PORTAL_HR_ORDER.peopleTable,
        trigger: { type: 'immediate' },
        // Only when the results table is actually rendered (a filtered-to-empty
        // result shows the empty-state card instead).
        condition: () => onPortalPath(PEOPLE_PATH) && readyRef.current && hasEmployeesRef.current,
        anchorId: PORTAL_HR_ANCHOR.peopleTable,
        placement: 'top',
        content: {
          body: "Each row shows a person's onboarding status. Use Send Welcome in the Action column to invite anyone who hasn't been.",
          dismissLabel: 'Got it',
        },
      }),
    ])

    // Registration alone does not trigger evaluation — start the flow so the
    // engine re-picks the candidate now these steps exist. Idempotent; the
    // layout hook (`use-portal-hr-ftue`) owns the real start logic.
    reevaluatePortalHrFlow()
  }, [])

  // Re-evaluate when the page state changes (a plain re-render doesn't re-run
  // the engine). If one of this hook's own steps is active but its `condition`
  // has flipped false and it hasn't been acknowledged yet, mark it seen so the
  // tour advances cleanly (the empty -> ready transition, chiefly) instead of
  // the coachmark just vanishing. Otherwise just re-pick the candidate. Mirrors
  // `use-learn-ftue`.
  // biome-ignore lint/correctness/useExhaustiveDependencies: ready/hasFilters/hasEmployees intentionally re-trigger evaluation; the effect reads live state via getState()
  useEffect(() => {
    const { activeStepId, seen, complete } = useFtueStore.getState()
    if (activeStepId && OWN_STEP_IDS.includes(activeStepId)) {
      const s = getStep(PORTAL_HR_ACTIVATION_FLOW, activeStepId)
      if (s?.condition && !s.condition() && !seen.has(activeStepId)) {
        complete(activeStepId)
        return
      }
    }
    reevaluatePortalHrFlow()
  }, [ready, hasFilters, hasEmployees])

  return {
    /** Spread onto the empty-state `<Card>`. */
    emptyAnchor,
    /** Spread onto the filter toolbar wrapper. */
    filtersAnchor,
    /** Spread onto the results `<Card>`. */
    tableAnchor,
  }
}
